import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

/**
 * Renders an email body in isolation, the way a mail client does.
 *
 * Why an iframe rather than `[innerHTML]`:
 *
 *   1. **Style isolation, both ways.** An email carries its own CSS — often
 *      `<style>` blocks and table layouts written for Outlook. Dropped into the
 *      page with `[innerHTML]` those rules land in the app's document and can
 *      restyle the surrounding UI, while the app's own styles (Material,
 *      Bootstrap grid, global resets) leak into the email and break its layout.
 *      An iframe gives the message its own document, so neither can reach the
 *      other.
 *
 *   2. **Fidelity.** Angular's sanitiser strips inline `style` attributes and
 *      `<style>` blocks, which is most of what an HTML email is made of, so the
 *      body renders as unstyled text. Inside the frame the message keeps its
 *      own formatting.
 *
 * Safety: the frame is sandboxed. It is given `allow-same-origin` — needed to
 * write the document and measure its height — but deliberately NOT
 * `allow-scripts`, so no script in the message can execute. That combination is
 * safe; `allow-same-origin` together with `allow-scripts` would NOT be, because
 * the message could then reach into this application. Do not add
 * `allow-scripts` to the sandbox attribute in the template.
 */
@Component({
  selector: 'app-email-body',
  templateUrl: './email-body.component.html',
  styleUrls: ['./email-body.component.scss'],
})
export class EmailBodyComponent implements AfterViewInit, OnChanges {

  /** Raw email HTML, as it came back from the API. */
  @Input() html: string;

  /** Tallest the frame grows before it scrolls internally. */
  @Input() maxHeight = 520;

  @ViewChild('frame') frameRef: ElementRef<HTMLIFrameElement>;

  /** Current frame height in px, grown to fit the message. */
  height = 120;

  private viewReady = false;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.html && this.viewReady) {
      this.render();
    }
  }

  // Summary
  // Write the message into the frame's own document. Writing directly rather
  // than binding `srcdoc` keeps Angular's sanitiser out of the path entirely --
  // the sandbox, not the sanitiser, is what makes this safe.
  private render(): void {
    if (!this.frameRef) {
      return;
    }

    const iframe = this.frameRef.nativeElement;
    const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
    if (!doc) {
      return;
    }

    doc.open();
    doc.write(this.wrap(this.html || ''));
    doc.close();

    // Text is laid out immediately; images arrive later and change the height,
    // so measure now and again once each image has loaded.
    this.measure(doc);
    const images = doc.querySelectorAll('img');
    for (let i = 0; i < images.length; i++) {
      images[i].addEventListener('load', () => this.measure(doc));
      images[i].addEventListener('error', () => this.measure(doc));
    }
  }

  /** Grow the frame to the message, up to `maxHeight`. */
  private measure(doc: Document): void {
    if (!doc.body) {
      return;
    }
    const needed = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
    this.height = Math.min(Math.max(needed + 8, 80), this.maxHeight);
  }

  // Summary
  // Wrap the message in a minimal document. These defaults only apply where the
  // email sets nothing of its own, so they never fight the message's styling.
  private wrap(body: string): string {
    return '<!doctype html><html><head><meta charset="utf-8">' +
      // links out of a sandboxed frame have to open in a new tab
      '<base target="_blank">' +
      '<style>' +
      'html,body{margin:0;padding:0;}' +
      "body{font-family:'Poppins','Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
      'font-size:14px;line-height:1.5;color:#1d1d1f;' +
      'word-wrap:break-word;overflow-wrap:break-word;}' +
      'img{max-width:100%;height:auto;}' +
      'table{max-width:100%;}' +
      '</style></head><body>' + body + '</body></html>';
  }
}
